import styled from 'foundations-theming-specialty/utils';
import unescapedText from 'I18n/utils/unescapedText';
import { View } from 'foundations-components/transitional/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { HR } from 'foundations-components/transitional/components/HR';
import { Clickable } from 'foundations-components/transitional/components/Clickable';
import { SidebarLeftCollapseIcon } from 'foundations-assets/icons/components/SidebarLeftCollapseIcon';
import { SidebarLeftExpandIcon } from 'foundations-assets/icons/components/SidebarLeftExpandIcon';
import { usePortalNavigation } from '../../hooks/usePortalNavigation';
import { useIsPillBorderRadius } from '../../hooks/useIsPillBorderRadius';
import { hasHsRouteKeyCookie } from '../../utils/hsRouteKey';
import { PortalsSideNavItem } from './PortalsSideNavItem';
import { SiteMenuNavItem } from './SiteMenuNavItem';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const HsRouteKeyTape = styled.div.withConfig({
  displayName: "PortalsSideNav__HsRouteKeyTape"
})({
  position: 'absolute',
  insetBlockEnd: '0px',
  inlineSize: '56px',
  blockSize: '12px',
  borderRadius: '0px',
  backgroundImage: 'repeating-linear-gradient(-45deg, #FCC200 0 6px, #1A1A1A 6px 12px)',
  cursor: 'help'
});

// Scoped styled button so the toggle matches the nav item icons exactly (same
// 40px box, border-radius token and 20px glyph) — the Trellis IconButton locks
// its glyph to 16px and applies its own radius. `user-select: none` stops rapid
// (double) clicks from starting a text selection that bleeds into the content.
const ToggleButton = styled.div.withConfig({
  displayName: "PortalsSideNav__ToggleButton"
})(({
  theme,
  $isPill
}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  inlineSize: '40px',
  blockSize: '40px',
  flexShrink: 0,
  borderRadius: theme.borderRadius[$isPill ? '0' : '300'],
  userSelect: 'none',
  transition: 'background-color 150ms',
  '&:hover, &[data-vr-state="hover"]': {
    backgroundColor: theme.color['fill-surface-default-hover']
  }
}));
export function PortalsSideNav({
  history,
  currentPathname,
  isExpanded = false,
  onToggleExpanded
}) {
  const {
    navItems,
    siteMenuData
  } = usePortalNavigation(currentPathname);
  const isPill = useIsPillBorderRadius();
  const showHsRouteKeyTape = hasHsRouteKeyCookie();
  const toggleLabel = unescapedText(isExpanded ? 'app.sideNav.collapse' : 'app.sideNav.expand');
  return /*#__PURE__*/_jsxs(View, {
    as: "nav",
    "aria-label": "Portal navigation",
    blockSize: "100%",
    children: [showHsRouteKeyTape && /*#__PURE__*/_jsx(HsRouteKeyTape, {
      title: "A preview route key is enabled",
      "aria-label": "A preview route key is enabled",
      "data-test-id": "portals-side-nav-hs-route-key-tape"
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      blockSize: "100%",
      gap: "400",
      paddingInline: "200",
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        alignItems: "stretch",
        gap: "400",
        children: [navItems.map(item => /*#__PURE__*/_jsx(PortalsSideNavItem, {
          item: item,
          history: history,
          isExpanded: isExpanded
        }, item.id)), onToggleExpanded && navItems.length > 0 && siteMenuData && siteMenuData.items.length > 0 && /*#__PURE__*/_jsx(View, {
          paddingInline: "100",
          children: /*#__PURE__*/_jsx(HR, {
            distance: "flush"
          })
        }), siteMenuData && siteMenuData.items.length > 0 && /*#__PURE__*/_jsx(SiteMenuNavItem, {
          menuData: siteMenuData,
          isExpanded: isExpanded
        })]
      }), /*#__PURE__*/_jsx(Flex, {
        flexGrow: "1"
      }), onToggleExpanded && /*#__PURE__*/_jsx(Flex, {
        alignItems: "start",
        children: /*#__PURE__*/_jsx(Clickable, {
          onClick: onToggleExpanded,
          "aria-label": toggleLabel,
          "aria-expanded": isExpanded,
          "data-test-id": "portals-side-nav-toggle",
          children: /*#__PURE__*/_jsx(ToggleButton, {
            $isPill: isPill,
            children: isExpanded ? /*#__PURE__*/_jsx(SidebarLeftCollapseIcon, {
              color: "text-core-default",
              size: "500"
            }) : /*#__PURE__*/_jsx(SidebarLeftExpandIcon, {
              color: "text-core-default",
              size: "500"
            })
          })
        })
      })]
    })]
  });
}